import React, { useCallback, useEffect, useState } from 'react';
import { useTrack } from './TrackLayout';
import { trackApi, errMsg } from '../../lib/trackApi';
import {
  C, btn, btnGhost, btnSm, btnLink, input as inputStyle, card,
} from '../../components/track/trackUi';

function BindPanel({ member, onDone, onCancel }) {
  const [q, setQ] = useState(member.phone || member.name || '');
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (q.trim().length < 2) { setList([]); return undefined; }
    const t = setTimeout(() => {
      trackApi.searchSiteMembers(q.trim())
        .then((r) => { setList(r?.data || []); setError(''); })
        .catch((e) => setError(errMsg(e)));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const bind = async (siteId) => {
    setBusy(true);
    try {
      await trackApi.bindMember(member.id, siteId);
      await onDone();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 8, padding: 10, background: '#f9fafb', borderRadius: 6 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
        <input autoFocus style={{ ...inputStyle, width: 220 }} placeholder="會員電話或姓名" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={onCancel}>取消</button>
      </div>
      {error && <div style={{ color: C.danger, fontSize: 12 }}>{error}</div>}
      {list.map((m) => (
        <div key={m.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, padding: '3px 0' }}>
          <span style={{ width: 110 }}>{m.name || '（未填姓名）'}</span>
          <span style={{ width: 110, color: C.muted }}>{m.phone}</span>
          <span style={{ width: 70, color: m.hasLine ? C.ok : C.muted, fontSize: 12 }}>{m.hasLine ? 'LINE ✓' : '無 LINE'}</span>
          <button type="button" style={{ ...btn, ...btnSm }} disabled={busy} onClick={() => bind(m.id)}>綁定</button>
        </div>
      ))}
      {q.trim().length >= 2 && !list.length && !error && <div style={{ fontSize: 12, color: C.muted }}>查無會員</div>}
    </div>
  );
}

function MemberRow({ m, first, last, onMove, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(m.name);
  const [phone, setPhone] = useState(m.phone);
  const [binding, setBinding] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setName(m.name); setPhone(m.phone); }, [m.name, m.phone]);

  const patch = async (body) => {
    try {
      await trackApi.patchMember(m.id, body);
      setError('');
      await onChanged();
      return true;
    } catch (e) {
      setError(errMsg(e));
      return false;
    }
  };

  const unbind = async () => {
    if (!window.confirm(`解除「${m.name}」的會員綁定？`)) return;
    try {
      await trackApi.bindMember(m.id, null);
      await onChanged();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  return (
    <div style={{ ...card, padding: 10, marginBottom: 8, opacity: m.isActive ? 1 : 0.55 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
          <button type="button" style={{ ...btnLink, color: C.muted }} disabled={first} onClick={() => onMove(-1)}>▲</button>
          <button type="button" style={{ ...btnLink, color: C.muted }} disabled={last} onClick={() => onMove(1)}>▼</button>
        </span>
        {edit ? (
          <>
            <input style={{ ...inputStyle, width: 120 }} value={name} onChange={(e) => setName(e.target.value)} />
            <input style={{ ...inputStyle, width: 130 }} placeholder="電話" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <button type="button" style={{ ...btn, ...btnSm }} onClick={async () => { if (await patch({ name, phone })) setEdit(false); }}>儲存</button>
            <button type="button" style={{ ...btnGhost, ...btnSm }} onClick={() => { setEdit(false); setName(m.name); setPhone(m.phone); }}>取消</button>
          </>
        ) : (
          <>
            <strong style={{ minWidth: 80 }}>{m.name}</strong>
            <span style={{ color: C.muted, fontSize: 13, minWidth: 100 }}>{m.phone || '—'}</span>
            <button type="button" style={btnLink} onClick={() => setEdit(true)}>編輯</button>
          </>
        )}

        <span style={{ fontSize: 13, minWidth: 160 }}>
          {m.memberId ? (
            <>
              會員：{m.memberName || `#${m.memberId}`}
              <button type="button" style={{ ...btnLink, marginLeft: 6, color: C.danger }} onClick={unbind}>解除</button>
            </>
          ) : (
            <button type="button" style={btnLink} onClick={() => setBinding((v) => !v)}>綁定會員</button>
          )}
        </span>

        <span
          style={{
            fontSize: 12, fontWeight: 700, padding: '1px 8px', borderRadius: 999,
            background: m.lineUserId ? '#dcfce7' : '#f3f4f6', color: m.lineUserId ? C.ok : C.muted,
          }}
        >
          {m.lineUserId ? 'LINE 已連結' : 'LINE 未連結'}
        </span>

        <label style={{ fontSize: 13, display: 'inline-flex', gap: 4, alignItems: 'center', marginLeft: 'auto' }}>
          <input type="checkbox" checked={m.personalNotify} disabled={!m.lineUserId && !m.personalNotify} onChange={(e) => patch({ personalNotify: e.target.checked })} />
          個人通知
        </label>
        <label style={{ fontSize: 13, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <input type="checkbox" checked={m.isActive} onChange={(e) => patch({ isActive: e.target.checked })} />
          啟用
        </label>
      </div>
      {error && <div style={{ color: C.danger, fontSize: 12, marginTop: 4 }}>{error}</div>}
      {binding && !m.memberId && (
        <BindPanel member={m} onCancel={() => setBinding(false)} onDone={async () => { setBinding(false); await onChanged(); }} />
      )}
    </div>
  );
}

export default function TrackMembers() {
  const { reloadMeta } = useTrack();
  const [list, setList] = useState([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await trackApi.listMembers();
      setList(r?.data || []);
      setError('');
    } catch (e) {
      setError(errMsg(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changed = async () => {
    await load();
    reloadMeta();
  };

  const add = async () => {
    if (!name.trim()) return;
    try {
      await trackApi.createMember({ name: name.trim(), phone: phone.trim() });
      setName('');
      setPhone('');
      await changed();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const visible = list.filter((m) => showInactive || m.isActive);

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= visible.length) return;
    const ids = visible.map((m) => m.id);
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    const rest = list.filter((m) => !ids.includes(m.id)).map((m) => m.id);
    try {
      await trackApi.reorderMembers([...ids, ...rest]);
      await changed();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  return (
    <div style={{ maxWidth: 960 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input style={{ ...inputStyle, width: 140 }} placeholder="成員名稱" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <input style={{ ...inputStyle, width: 140 }} placeholder="電話（選填）" value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
        <button type="button" style={btn} disabled={!name.trim()} onClick={add}>＋ 新增成員</button>
        <label style={{ fontSize: 13, display: 'inline-flex', gap: 4, alignItems: 'center', marginLeft: 'auto' }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          顯示停用
        </label>
      </div>
      {error && <div style={{ color: C.danger, marginBottom: 8 }}>{error}</div>}
      {visible.map((m, i) => (
        <MemberRow
          key={m.id}
          m={m}
          first={i === 0}
          last={i === visible.length - 1}
          onMove={(dir) => move(i, dir)}
          onChanged={changed}
        />
      ))}
      {!visible.length && <div style={{ color: C.muted }}>尚無成員</div>}
    </div>
  );
}
